/**
 * WidgetChatPage - Embeddable chat widget for external websites
 * Chat is FREE - Verification only required for BOOKING
 */
import React, { useState, useEffect, useRef } from 'react';
import { Send, Loader2, Mail, MessageSquare, ArrowLeft, Calendar, Clock, User, Shield } from 'lucide-react';
import { et, etp, EMBED_LANG } from '../i18n/embedLang';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Rendert **fett** aus Bot-Antworten als <strong> (Widget zeigt sonst rohe Sternchen)
const renderBotText = (text) => {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i}>{part.slice(2, -2)}</strong>
      : part
  );
};


const WidgetChatPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('chat'); // chat (default), verification, verify-code, booking
  const [verificationMethod, setVerificationMethod] = useState('email');
  const [contact, setContact] = useState('');
  const [code, setCode] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [customerName, setCustomerName] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [lastBookingId, setLastBookingId] = useState(null);  // Track last booking for cancel
  const [cancelLoading, setCancelLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    // Only scroll for new messages, not initial load
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    // Scroll within the chat container only, not the parent page
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages]);

  const loadSettings = async () => {
    try {
      const response = await fetch(`${API}/widget/settings/public`);
      const data = await response.json();
      setSettings(data);
      
      // Always start with chat - verification only for booking
      const newSessionId = 'widget-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
      setSessionId(newSessionId);
      setStep('chat');
      const welcome = EMBED_LANG === 'de'
        ? data.welcome_message
        : `${et('Hallo! 👋 Wie kann ich dir helfen?')}\n\n🌍 Deutsch · English · Français · Italiano`;
      setMessages([{ role: 'assistant', content: welcome }]);
      
    } catch (err) {
      console.error('Load settings error:', err);
      setError(et('Widget konnte nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const requestVerification = async () => {
    if (!contact.trim()) {
      setError(verificationMethod === 'email' ? 'Bitte E-Mail eingeben' : 'Bitte Telefonnummer eingeben');
      return;
    }
    
    setVerifying(true);
    setError(null);
    
    try {
      // Use XMLHttpRequest to avoid rrweb-recorder fetch interception
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API}/widget/verify/request`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const responseData = JSON.parse(xhr.responseText);
              resolve(responseData);
            } catch (e) {
              reject(new Error(et('Server-Antwort konnte nicht verarbeitet werden')));
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              reject(new Error(errorData.detail || et('Verifizierung fehlgeschlagen')));
            } catch (e) {
              reject(new Error(`Server Error: ${xhr.status}`));
            }
          }
        };
        
        xhr.onerror = function() {
          reject(new Error(et('Netzwerkfehler')));
        };
        
        xhr.send(JSON.stringify({ method: verificationMethod, contact }));
      });
      
      setSessionId(data.session_id);
      setStep('verify-code');
      
      if (!data.delivered) {
        setError(et('Code generiert, aber Zustellung fehlgeschlagen. Bitte erneut versuchen.'));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setVerifying(false);
    }
  };

  const verifyCode = async () => {
    if (!code.trim() || code.length !== 6) {
      setError(et('Bitte 6-stelligen Code eingeben'));
      return;
    }
    
    setVerifying(true);
    setError(null);
    
    try {
      // Use XMLHttpRequest to avoid rrweb-recorder fetch interception
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API}/widget/verify/confirm`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const responseData = JSON.parse(xhr.responseText);
              resolve(responseData);
            } catch (e) {
              reject(new Error(et('Server-Antwort konnte nicht verarbeitet werden')));
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              reject(new Error(errorData.detail || et('Code ungültig')));
            } catch (e) {
              reject(new Error(`Server Error: ${xhr.status}`));
            }
          }
        };
        
        xhr.onerror = function() {
          reject(new Error(et('Netzwerkfehler')));
        };
        
        xhr.send(JSON.stringify({ session_id: sessionId, code }));
      });
      
      setCustomerName(data.customer_name);
      setIsVerified(true);
      setStep('chat');
      
      // Add verification success message
      const successMsg = data.customer_name 
        ? etp('✅ Verifiziert! Willkommen zurück, {0}! Du kannst jetzt Termine buchen.', [data.customer_name])
        : et('✅ Verifiziert! Du kannst jetzt Termine buchen.');
      
      setMessages(prev => [...prev, { role: 'assistant', content: successMsg }]);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setVerifying(false);
    }
  };

  // Start booking flow - show verification if not verified
  const startBooking = () => {
    if (isVerified) {
      // Already verified - show booking options
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: et('📅 Super! Welchen Service möchtest du buchen? Schau auf unserer Buchungsseite vorbei oder schreib mir, welchen Service du möchtest.') 
      }]);
    } else {
      // Need verification first
      setStep('verification');
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || sending) return;
    
    const userMessage = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setSending(true);
    
    try {
      console.log('🤖 Widget Chat - Sending message:', userMessage);
      console.log('   Session ID:', sessionId);
      console.log('   API URL:', `${API}/widget/chat`);
      
      // Use XMLHttpRequest to avoid rrweb-recorder fetch interception
      const response = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API}/widget/chat`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        
        xhr.onload = function() {
          console.log('   XHR Status:', xhr.status);
          console.log('   XHR Response:', xhr.responseText.substring(0, 200));
          
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              resolve(data);
            } catch (e) {
              reject(new Error(et('Server-Antwort konnte nicht verarbeitet werden')));
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              reject(new Error(errorData.detail || `Server Error: ${xhr.status}`));
            } catch (e) {
              reject(new Error(`Server Error: ${xhr.status}`));
            }
          }
        };
        
        xhr.onerror = function() {
          console.error('   XHR Network Error');
          reject(new Error(et('Netzwerkfehler')));
        };
        
        xhr.send(JSON.stringify({ session_id: sessionId, message: userMessage, lang: EMBED_LANG !== 'de' ? EMBED_LANG : undefined }));
      });
      
      console.log('✅ Widget Chat - Response received:', response);
      
      // Show AI response
      setMessages(prev => [...prev, { role: 'assistant', content: response.response }]);
      
      // If booking was successful, show confirmation message
      if (response.booking && response.booking.success) {
        const booking = response.booking;
        setLastBookingId(booking.appointment_id);  // Store for potential cancellation
        const confirmationMsg = etp('✅ **Termin gebucht!**\n\n📅 {0}\n🗓️ {1} um {2} Uhr\n👤 {3}\n\nDu erhältst eine Bestätigung per E-Mail.', [booking.service_name, booking.date, booking.time, booking.customer_name])
          + (booking.consent_link ? etp('\n\n✍️ **Wichtig:** Für diese Leistung brauchen wir vor deinem Termin noch deine unterschriebene Einverständnis-Erklärung (dauert 1 Minute):\n{0}\nDer Link kommt auch per E-Mail.', [booking.consent_link]) : '');
        setTimeout(() => {
          setMessages(prev => [...prev, { role: 'booking-success', content: confirmationMsg, bookingId: booking.appointment_id, booking: booking }]);
        }, 500);
      } else if (response.booking && !response.booking.success) {
        // Booking failed - show error to user
        const errorMsg = response.booking.error || et('Buchung fehlgeschlagen');
        const errorDisplay = etp('❌ **Buchung nicht möglich**\n\n{0}\n\nBitte wähle eine andere Zeit oder kontaktiere uns direkt.', [errorMsg]);
        setTimeout(() => {
          setMessages(prev => [...prev, { role: 'system', content: errorDisplay }]);
        }, 500);
      }
      
    } catch (err) {
      console.error('❌ Widget Chat Exception:', err.message);
      setMessages(prev => [...prev, { role: 'assistant', content: et('Entschuldigung, es ist ein Fehler aufgetreten. Bitte versuche es erneut.') }]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (step === 'verification') {
        requestVerification();
      } else if (step === 'verify-code') {
        verifyCode();
      } else {
        sendMessage();
      }
    }
  };

  // Cancel appointment function
  const cancelAppointment = async (appointmentId) => {
    if (!appointmentId) return;
    
    setCancelLoading(true);
    try {
      const response = await fetch(`${API}/widget/cancel-appointment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          appointment_id: appointmentId
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Show cancellation success
        setMessages(prev => [...prev, { 
          role: 'system', 
          content: etp('✅ **Termin storniert**\n\nDein Termin "{0}" wurde erfolgreich storniert.', [data.appointment?.service_name]) 
        }]);
        setLastBookingId(null);
      } else if (data.can_cancel === false) {
        // Less than 12 hours - show contact info
        setMessages(prev => [...prev, { 
          role: 'system', 
          content: etp('⚠️ **Stornierung nicht möglich**\n\n{0}\n\n📞 {1}', [data.message, data.contact_info?.action]) 
        }]);
      } else {
        // Other error
        setMessages(prev => [...prev, { 
          role: 'system', 
          content: `❌ **Fehler**\n\n${data.error || et('Stornierung fehlgeschlagen')}` 
        }]);
      }
    } catch (err) {
      console.error('Cancel error:', err);
      setMessages(prev => [...prev, { 
        role: 'system', 
        content: et('❌ Fehler bei der Stornierung. Bitte versuche es erneut.') 
      }]);
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) {
    return (
      <div 
        className="flex items-center justify-center h-screen"
        style={{ backgroundColor: settings?.background_color || '#1A1A1A' }}
      >
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: settings?.primary_color || '#C9A227' }} />
      </div>
    );
  }

  if (!settings?.enabled) {
    return (
      <div 
        className="flex items-center justify-center h-screen text-white"
        style={{ backgroundColor: settings?.background_color || '#1A1A1A' }}
      >
        <p>{et('Chat ist momentan nicht verfügbar')}</p>
      </div>
    );
  }

  const primaryColor = settings?.primary_color || '#C9A227';
  const bgColor = settings?.background_color || '#1A1A1A';

  return (
    <div 
      className="flex flex-col h-screen"
      style={{ backgroundColor: bgColor, fontFamily: 'system-ui, -apple-system, sans-serif' }}
    >
      {/* Header */}
      <div 
        className="p-4 flex items-center gap-3 shadow-lg"
        style={{ backgroundColor: primaryColor }}
      >
        {step !== 'verification' && step !== 'chat' && (
          <button 
            onClick={() => setStep('verification')}
            className="p-1 rounded hover:bg-white/20 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
        )}
        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
          <MessageSquare className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-white font-bold">{settings?.widget_title || 'StrongFit Chat'}</h1>
          <p className="text-white/70 text-sm">{et('Online')}</p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Verification Step - Only shown when user wants to book */}
        {step === 'verification' && (
          <div className="max-w-sm mx-auto mt-8 space-y-6">
            <div className="text-center">
              <div 
                className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: primaryColor + '20' }}
              >
                <Shield className="w-8 h-8" style={{ color: primaryColor }} />
              </div>
              <h2 className="text-white text-xl font-bold mb-2">{et('Verifizierung für Buchung')}</h2>
              <p className="text-gray-400 text-sm">
                {et('Um einen Termin zu buchen, verifiziere dich bitte kurz.')}
              </p>
            </div>

            {/* Back to Chat Button */}
            <button
              onClick={() => setStep('chat')}
              className="w-full p-2 text-gray-400 hover:text-white text-sm transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" /> {et('Zurück zum Chat')}
            </button>

            {/* Method Selection */}
            {settings?.verification_methods?.length > 1 && (
              <div className="flex gap-2">
                {settings.verification_methods.includes('email') && (
                  <button
                    onClick={() => setVerificationMethod('email')}
                    className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                      verificationMethod === 'email' 
                        ? 'border-opacity-100' 
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                    style={{ 
                      borderColor: verificationMethod === 'email' ? primaryColor : undefined,
                      backgroundColor: verificationMethod === 'email' ? primaryColor + '20' : 'transparent'
                    }}
                  >
                    <Mail className={`w-5 h-5 mx-auto mb-1 ${verificationMethod === 'email' ? '' : 'text-gray-500'}`} style={{ color: verificationMethod === 'email' ? primaryColor : undefined }} />
                    <span className={`text-sm ${verificationMethod === 'email' ? 'text-white' : 'text-gray-400'}`}>E-Mail</span>
                  </button>
                )}
                {settings.verification_methods.includes('whatsapp') && (
                  <button
                    onClick={() => setVerificationMethod('whatsapp')}
                    className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                      verificationMethod === 'whatsapp' 
                        ? 'border-[#25D366]' 
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                    style={{ backgroundColor: verificationMethod === 'whatsapp' ? '#25D36620' : 'transparent' }}
                  >
                    <MessageSquare className={`w-5 h-5 mx-auto mb-1 ${verificationMethod === 'whatsapp' ? 'text-[#25D366]' : 'text-gray-500'}`} />
                    <span className={`text-sm ${verificationMethod === 'whatsapp' ? 'text-white' : 'text-gray-400'}`}>WhatsApp</span>
                  </button>
                )}
              </div>
            )}

            {/* Input */}
            <div className="space-y-2">
              <input
                type={verificationMethod === 'email' ? 'email' : 'tel'}
                placeholder={verificationMethod === 'email' ? et('Deine E-Mail-Adresse') : et('Deine Handynummer (+41...)')}
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2"
                style={{ focusRingColor: primaryColor }}
              />
              
              {error && (
                <p className="text-red-400 text-sm">{error}</p>
              )}

              <button
                onClick={requestVerification}
                disabled={verifying}
                className="w-full p-3 rounded-lg text-white font-medium transition-colors disabled:opacity-50"
                style={{ backgroundColor: primaryColor }}
              >
                {verifying ? (
                  <Loader2 className="w-5 h-5 mx-auto animate-spin" />
                ) : (
                  et('Code senden')
                )}
              </button>
            </div>
          </div>
        )}

        {/* Verify Code Step */}
        {step === 'verify-code' && (
          <div className="max-w-sm mx-auto mt-8 space-y-6">
            <div className="text-center">
              <div 
                className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: primaryColor + '20' }}
              >
                <Mail className="w-8 h-8" style={{ color: primaryColor }} />
              </div>
              <h2 className="text-white text-xl font-bold mb-2">{et('Code eingeben')}</h2>
              <p className="text-gray-400 text-sm">
                {etp('Wir haben dir einen 6-stelligen Code an {0} gesendet.', [contact])}
              </p>
            </div>

            <div className="space-y-2">
              <input
                type="text"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyPress={handleKeyPress}
                className="w-full p-4 rounded-lg bg-gray-800 text-white text-center text-2xl tracking-widest border border-gray-700 focus:outline-none focus:ring-2"
                style={{ focusRingColor: primaryColor }}
                maxLength={6}
              />
              
              {error && (
                <p className="text-red-400 text-sm text-center">{error}</p>
              )}

              <button
                onClick={verifyCode}
                disabled={verifying || code.length !== 6}
                className="w-full p-3 rounded-lg text-white font-medium transition-colors disabled:opacity-50"
                style={{ backgroundColor: primaryColor }}
              >
                {verifying ? (
                  <Loader2 className="w-5 h-5 mx-auto animate-spin" />
                ) : (
                  et('Bestätigen')
                )}
              </button>

              <button
                onClick={() => { setStep('verification'); setCode(''); setError(null); }}
                className="w-full p-2 text-gray-400 hover:text-white text-sm transition-colors"
              >
                {et('Code erneut senden')}
              </button>
            </div>
          </div>
        )}

        {/* Chat Step */}
        {step === 'chat' && (
          <div className="space-y-4">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div 
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    msg.role === 'user' 
                      ? 'rounded-br-sm text-white' 
                      : msg.role === 'system' || msg.role === 'booking-success'
                        ? 'bg-green-800 text-white rounded-bl-sm border-2 border-green-500'
                        : 'bg-gray-800 text-white rounded-bl-sm'
                  }`}
                  style={{ backgroundColor: msg.role === 'user' ? primaryColor : undefined }}
                >
                  <p className="whitespace-pre-wrap text-sm">{msg.role === 'assistant' ? renderBotText(msg.content) : msg.content}</p>
                  
                  {/* Cancel Button for successful bookings */}
                  {msg.role === 'booking-success' && msg.bookingId && (
                    <button
                      onClick={() => cancelAppointment(msg.bookingId)}
                      disabled={cancelLoading}
                      className="mt-3 w-full py-2 px-4 rounded-full text-sm font-medium transition-colors flex items-center justify-center gap-2"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {cancelLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>{et('stornieren')}</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
            
            {sending && (
              <div className="flex justify-start">
                <div className="bg-gray-800 p-3 rounded-2xl rounded-bl-sm">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input (only in chat) */}
      {step === 'chat' && (
        <div className="p-4 border-t border-gray-800 space-y-3">
          {/* Booking Button - shows verification status */}
          {settings?.booking_enabled && (
            <div className="flex gap-2">
              <button
                onClick={startBooking}
                className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  isVerified 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <Calendar className="w-4 h-4" />
                {isVerified ? et('✅ Termin buchen') : et('📅 Termin buchen')}
              </button>
              {!isVerified && (
                <span className="text-xs text-gray-500 flex items-center">
                  <Shield className="w-3 h-3 mr-1" /> {et('Verifizierung nötig')}
                </span>
              )}
            </div>
          )}
          
          {/* Message Input */}
          <div className="flex gap-2">
            <input
              type="text"
              placeholder={et('Nachricht schreiben...')}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={sending}
              className="flex-1 p-3 rounded-full bg-gray-800 text-white placeholder-gray-500 focus:outline-none focus:ring-2"
              style={{ focusRingColor: primaryColor }}
            />
            <button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || sending}
              className="p-3 rounded-full text-white transition-colors disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="p-2 text-center">
        <p className="text-gray-600 text-xs">
          Powered by Logikwerk
        </p>
      </div>
    </div>
  );
};

export default WidgetChatPage;
